import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { PublicUserEntity } from './public-user.entity';

/**
 * PD-16475 — one browser a storefront user subscribed for push notifications.
 * The portal counterpart of `user_notification_device`; the same push sender
 * delivers to both (PushSenderService.sendToSubscriptions).
 *
 * Unique per (user, endpoint), so a browser that subscribes again refreshes
 * its row instead of adding another.
 */
@Index('public_user_notification_device_user_idx', ['fkPublicUserId'])
@Index(
	'public_user_notification_device_user_endpoint_idx',
	['fkPublicUserId', 'endpoint'],
	{ unique: true },
)
@Entity('public_user_notification_device')
export class PublicUserNotificationDeviceEntity extends BaseEntity {
	@Column('int', { name: 'fk_public_user__id', unsigned: true })
	public fkPublicUserId!: number;

	/**
	 * `'web'` for browser push. Free-form so native app push (PD-14302) can add
	 * its platforms without a schema change.
	 */
	@Column('varchar', { name: 'platform', length: 32 })
	public platform!: string;

	/**
	 * The push service URL from the browser's PushSubscription. varchar(512),
	 * not text, so it can sit in the unique index without a prefix length.
	 */
	@Column('varchar', { name: 'endpoint', length: 512 })
	public endpoint!: string;

	/** PushSubscription.keys.p256dh — the browser's key for payload encryption. */
	@Column('varchar', { name: 'p256dh', length: 255 })
	public p256dh!: string;

	/** PushSubscription.keys.auth — the browser's auth secret. */
	@Column('varchar', { name: 'auth', length: 255 })
	public auth!: string;

	/** Native push token for a future app platform (PD-14302); null for browsers. */
	@Column('varchar', { name: 'deviceToken', length: 512, nullable: true })
	public deviceToken!: string | null;

	@Column('varchar', { name: 'userAgent', length: 512, nullable: true })
	public userAgent!: string | null;

	/** Last time the browser subscribed or refreshed its subscription. */
	@Column('datetime', { name: 'lastUsedDate' })
	public lastUsedDate!: Date;

	/** PushSubscription.expirationTime; the sender skips and prunes expired rows. */
	@Column('datetime', { name: 'expiresAt', nullable: true })
	public expiresAt!: Date | null;

	@ManyToOne(() => PublicUserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_public_user__id', referencedColumnName: 'id' }])
	public fkPublicUser!: PublicUserEntity;
}
